// Makes a map tile-sprite (a tree/bush/reed canopy, or a wall) translucent while the LOCAL player is
// standing behind it, so the player is never fully hidden. Purely a client-side visual: it only
// tweaks each sprite's `.alpha` (the same handle the build-preview ghost uses) and never touches game
// state, z-order, or the tile-effect system (which does not read/write alpha).
//
// Occlusion is decided the same way the engine already decides draw order -- a y-sort. A tile sprite
// sorts in front of the player when its zIndex is greater (see sprite_management_service.createSpriteAt
// and entity_mechanics.calculateZIndexBoost). We fade exactly the sprites that (a) sort in front and
// (b) whose box overlaps the player's body box. That is self-limiting: the trunk below the player and
// trees lower on screen never overlap the body box, so only the canopy actually covering the player
// fades.
//
// The candidate sprites are the pooled tile sprites (trees/bushes) plus the owned sprited-cell sprites
// (walls). Both persist between map re-renders, so we track what we faded and ease it back to opaque
// once the player steps out. The sprite pool resets alpha to 1.0 when it recycles a sprite, so a stale
// tracked reference can never leave a tile stuck translucent.

// Master on/off switch for the whole tree occluder-fade feature. Flip to false to ship it disabled:
// it becomes the default for `enabled`, and the per-frame pass short-circuits to a no-op (no pool scan,
// no fading). For toggling at runtime without a rebuild, set window.occluderFade.enabled instead.
export const OCCLUDER_FADE_ENABLED = true

export interface OccluderFadeConfig {
    // Master toggle (live-tunable via window.occluderFade).
    enabled: boolean
    // Target alpha applied to an occluding tile (0..1). 0.5 matches the build-preview ghost.
    alpha: number
    // Per-frame lerp factor toward the target alpha (0..1). This is what makes affected trees ease
    // (fade) in and out instead of popping: each frame alpha moves `speed` of the way to its target,
    // an exponential ease-out. Smaller = slower, more graceful fade. ~0.10 ≈ a half-second fade at 60fps.
    speed: number
    // Neighbor-density gate. We only fade when the player is inside a dense stand of trees, so the
    // "am I see-through right now?" answer is a stable, regional property rather than a per-step
    // coin-flip. Density = the number of tile sprites whose center sits within `neighborRadius` px of
    // the player. The gate turns ON at `minNeighbors` and OFF only once density falls to
    // `minNeighbors - hysteresis` -- the gap prevents flicker when the player lingers on the boundary.
    neighborRadius: number
    minNeighbors: number
    hysteresis: number
}

// Defaults calibrated live in the forest biome: its trees are large single sprites, so a dense stand
// only yields ~14 sprite centers within a 5-tile radius while a lone tree yields ~2 -- minNeighbors
// sits between them. Tune live via window.occluderFade.
export const defaultOccluderFadeConfig = (): OccluderFadeConfig => ({
    enabled: OCCLUDER_FADE_ENABLED,
    alpha: 0.5,
    speed: 0.1,
    neighborRadius: 160,
    minNeighbors: 8,
    hysteresis: 3,
})

// Player body box in fine (pixel) coords, anchored at the feet sort-point (== entity.location). The
// box spans up from the feet to roughly the head so a canopy overlapping the torso/head counts as
// occluding, while a trunk sprite sitting at/below the feet line does not.
const PLAYER_HALF_WIDTH = 11
const PLAYER_BODY_HEIGHT = 46
const PLAYER_FEET_MARGIN = 2

// Alpha within this of the target snaps exactly (and, when restoring, drops the sprite from tracking).
const SNAP = 0.02

// Minimal shape we rely on; PIXI's Sprite satisfies it. Kept structural so this stays unit-testable
// without a real renderer.
export interface OccludableSprite {
    x: number
    y: number
    width: number
    height: number
    zIndex: number
    alpha: number
    destroyed: boolean
}

export class EntityTileOccluderFade {
    readonly config: OccluderFadeConfig = defaultOccluderFadeConfig()
    // Sprites currently pushed off full opacity; tracked until they lerp back to 1.0 so we know which
    // to restore once the player steps out from behind them.
    private tracked = new Set<OccludableSprite>()
    // The occluders found this frame. Reused (cleared) every frame rather than reallocated, so the
    // per-frame path allocates nothing -- avoids the steady GC pressure that causes hitches on
    // low-end machines (see the budget-hardware note).
    private occluding = new Set<OccludableSprite>()
    // Latched gate state (hysteresis) + last density, exposed for the debug/tuning readout.
    private denseLatched = false
    lastNeighborCount = 0

    // Whether the player is currently in a "dense" stand (the fade is active).
    get isDense(): boolean {
        return this.denseLatched
    }

    // Fade the tile sprites the player (feet at fine coords feetX/feetY, draw-order key playerZIndex)
    // is behind, and ease everything else back to opaque. `candidates` is the tree sprites currently on
    // screen (a reused array, so no allocation). One indexed pass computes both the local tree density
    // (neighbor count) and the set of tiles overlapping the player; the density gate then decides
    // whether those overlappers fade. Allocation-free and idempotent per frame.
    update(feetX: number, feetY: number, playerZIndex: number, candidates: readonly OccludableSprite[]): void {
        if (!this.config.enabled) {
            this.relaxAll()
            return
        }

        const bx1 = feetX - PLAYER_HALF_WIDTH
        const bx2 = feetX + PLAYER_HALF_WIDTH
        const by1 = feetY - PLAYER_BODY_HEIGHT
        const by2 = feetY - PLAYER_FEET_MARGIN
        const radius = this.config.neighborRadius
        const r2 = radius * radius

        let neighbors = 0
        const occluding = this.occluding
        occluding.clear()
        for (let i = 0; i < candidates.length; i++) {
            const g = candidates[i]
            if (!g || g.destroyed) {
                continue
            }
            const w = g.width
            const h = g.height
            if (w <= 0 || h <= 0) {
                continue
            }
            // Local density: does this tile's center sit within neighborRadius of the player? Skip the
            // squared-distance for tiles already too far in x to qualify (the common case off-cluster).
            const dx = g.x + w * 0.5 - feetX
            if (dx <= radius && dx >= -radius) {
                const dy = g.y + h * 0.5 - feetY
                if (dx * dx + dy * dy <= r2) {
                    neighbors++
                }
            }
            // Occlusion: draws in front AND its box (top-left anchored) overlaps the body box. Cheap
            // comparisons that short-circuit for far tiles, so this stays correct at any neighborRadius.
            if (g.zIndex > playerZIndex && g.x < bx2 && g.x + w > bx1 && g.y < by2 && g.y + h > by1) {
                occluding.add(g)
            }
        }
        this.lastNeighborCount = neighbors

        // Neighbor-density gate with hysteresis: turn ON at minNeighbors, OFF only once density falls
        // to minNeighbors - hysteresis, so the player lingering on the boundary can't cause a blink.
        const onThreshold = this.config.minNeighbors
        const offThreshold = Math.max(0, this.config.minNeighbors - this.config.hysteresis)
        this.denseLatched = this.denseLatched ? neighbors >= offThreshold : neighbors >= onThreshold
        const dense = this.denseLatched

        if (dense) {
            occluding.forEach(g => this.tracked.add(g))
        }
        for (const g of this.tracked) {
            if (g.destroyed) {
                this.tracked.delete(g)
                continue
            }
            const target = dense && occluding.has(g) ? this.config.alpha : 1.0
            const next = g.alpha + (target - g.alpha) * this.config.speed
            if (Math.abs(next - target) <= SNAP) {
                g.alpha = target
                if (target >= 1.0) {
                    this.tracked.delete(g)
                }
            } else {
                g.alpha = next
            }
        }
    }

    // Ease every tracked sprite back to full opacity (used when the feature is disabled or there is no
    // player). Idempotent; drops sprites once restored so no sprite references are retained while idle.
    relaxAll(): void {
        // Drop last frame's occluder refs -- the active path clears this each frame, but the disabled
        // path skips it, so clear here too and hold nothing while idle.
        this.occluding.clear()
        this.denseLatched = false
        for (const g of this.tracked) {
            if (g.destroyed) {
                this.tracked.delete(g)
                continue
            }
            const next = g.alpha + (1.0 - g.alpha) * this.config.speed
            if (1.0 - next <= SNAP) {
                g.alpha = 1.0
                this.tracked.delete(g)
            } else {
                g.alpha = next
            }
        }
    }
}
